import { useState, useEffect, useCallback } from 'react'
import { Upload, Eye, Rocket, Loader2, Check, AlertCircle, Edit3, X, Zap, Star, Sparkles } from 'lucide-react'
import { apiClient } from '../utils/api'

function SiteGenerator({ onGenerate, fullScreen = false, splitView = false, initialContent = null, initialFormData = null, onPublish, onShowEditDialog }) {
  const [formData, setFormData] = useState({
    tokenName: '',
    tokenSymbol: '',
    contractAddress: '',
    description: '',
    socialLinks: {
      twitter: '',
      telegram: '',
      discord: '',
      website: ''
    },
    dexPlatforms: {
      pumpswap: false,
      pancakeswap: false,
      raydium: false,
      jupiter: false,
      dexscreener: false
    },
    logo: null
  })
  
  const [currentStep, setCurrentStep] = useState('form') // form, preview, publishing, published
  const [isGenerating, setIsGenerating] = useState(false)
  const [previewHtml, setPreviewHtml] = useState('')
  const [generatedUrl, setGeneratedUrl] = useState('')
  const [error, setError] = useState('')
  const [showDiffDialog, setShowDiffDialog] = useState(false)
  const [diffRequest, setDiffRequest] = useState('')
  const [isApplyingDiff, setIsApplyingDiff] = useState(false)

  // Handle initial content for split view
  useEffect(() => {
    if (initialContent && splitView) {
      setPreviewHtml(initialContent)
      setCurrentStep('preview')
    }
    // Set form data from initial form data if provided (split view)
    if (initialFormData && splitView) {
      setFormData(initialFormData)
    }
  }, [initialContent, initialFormData, splitView])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSocialChange = (platform, value) => {
    setFormData(prev => ({
      ...prev,
      socialLinks: {
        ...prev.socialLinks,
        [platform]: value
      }
    }))
  }

  const handleDexChange = (platform, checked) => {
    setFormData(prev => ({
      ...prev,
      dexPlatforms: {
        ...prev.dexPlatforms,
        [platform]: checked
      }
    }))
  }

  const handleLogoChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload a valid image file (JPEG, PNG, GIF, or WebP)')
        return
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image file must be less than 5MB')
        return
      }
      
      setFormData(prev => ({
        ...prev,
        logo: file
      }))
      setError('')
    }
  }

  const generatePreview = async () => {
    setIsGenerating(true)
    setError('')
    setPreviewHtml('') // Clear previous preview
    
    try {
      const formDataToSend = new FormData()
      formDataToSend.append('tokenName', formData.tokenName)
      formDataToSend.append('tokenSymbol', formData.tokenSymbol)
      formDataToSend.append('contractAddress', formData.contractAddress)
      formDataToSend.append('description', formData.description)
      formDataToSend.append('socialLinks', JSON.stringify(formData.socialLinks))
      formDataToSend.append('dexPlatforms', JSON.stringify(formData.dexPlatforms))
      
      if (formData.logo) {
        formDataToSend.append('logo', formData.logo)
      }

      console.log('Sending generation request...')
      const response = await apiClient.generatePreview(formDataToSend)
      
      console.log('Generation completed, setting preview HTML...')
      setPreviewHtml(response.html)
      setCurrentStep('preview')
      
      // Trigger phase transition in GeneratorPage
      if (onGenerate && fullScreen) {
        onGenerate(response.html, formData)
      }
    } catch (err) {
      console.error('Preview generation error:', err)
      if (err.code === 'ECONNABORTED') {
        setError('Generation timed out. Please try again - AI generation can take up to 2 minutes.')
      } else if (err.response?.status === 400) {
        setError(err.response?.data?.detail || 'Invalid request. Please check your inputs.')
      } else if (err.response?.status === 500) {
        setError('Server error during generation. Please try again.')
      } else {
        setError('Failed to generate preview. Please check your connection and try again.')
      }
    } finally {
      setIsGenerating(false)
    }
  }

  const publishSite = useCallback(async () => {
    console.log('🚀 PublishSite called - tokenName:', formData.tokenName, 'previewHtml length:', previewHtml?.length)
    
    if (!previewHtml) {
      setError('No site content to publish. Please generate a site first.')
      return
    }
    
    if (!formData.tokenName) {
      setError('Token name is required to publish.')
      return
    }
    
    setIsGenerating(true)
    setError('')
    
    try {
      console.log('📤 Sending publish request...')
      const response = await apiClient.publishSite({
        tokenName: formData.tokenName,
        html: previewHtml
      })
      
      console.log('✅ Publish successful:', response)
      setGeneratedUrl(response.url)
      setCurrentStep('published')
    } catch (err) {
      console.error('❌ Publish error details:', err)
      console.error('Error response:', err.response?.data)
      console.error('Error status:', err.response?.status)
      
      if (err.response?.status === 500) {
        setError(`Server error: ${err.response?.data?.detail || 'Internal server error during publishing'}`)
      } else if (err.response?.data?.detail) {
        setError(`Publish failed: ${err.response.data.detail}`)
      } else {
        setError('Failed to publish site. Please try again.')
      }
    } finally {
      setIsGenerating(false)
    }
  }, [formData.tokenName, previewHtml])

  // Expose functions to GeneratorPage
  useEffect(() => {
    if (splitView && onPublish) {
      onPublish(publishSite)
    }
    if (splitView && onShowEditDialog) {
      onShowEditDialog(() => setShowDiffDialog(true))
    }
  }, [splitView, onPublish, onShowEditDialog, publishSite])

  const applyDiff = async () => {
    if (!diffRequest.trim()) return
    
    setIsApplyingDiff(true)
    setError('')
    
    try {
      console.log('🔄 Applying diff request:', diffRequest)
      console.log('📄 Current HTML length:', previewHtml.length)
      
      const formDataToSend = new FormData()
      formDataToSend.append('current_html', previewHtml)
      formDataToSend.append('diff_request', diffRequest)
      
      // Log FormData contents for debugging
      for (let [key, value] of formDataToSend.entries()) {
        console.log(`FormData ${key}:`, typeof value === 'string' ? value.substring(0, 100) + '...' : value)
      }
      
      const response = await apiClient.applyDiff(formDataToSend)
      
      if (response.success) {
        setPreviewHtml(response.html)
        setDiffRequest('')
        setShowDiffDialog(false)
        
        // Update preview in split view
        if (onGenerate && splitView) {
          onGenerate(response.html, formData)
        }
      } else {
        setError('Failed to apply changes. Please try again.')
      }
    } catch (err) {
      console.error('Diff application error:', err)
      if (err.response?.status === 422) {
        console.error('Validation error details:', err.response.data)
        setError('Invalid request format. Please try again.')
      } else if (err.response?.status === 500) {
        setError('Server error during diff application. Please try again.')
      } else {
        setError('Failed to apply changes. Please check your connection and try again.')
      }
    } finally {
      setIsApplyingDiff(false)
    }
  }

  const resetForm = () => {
    // Clean up any object URLs to prevent memory leaks
    if (formData.logo) {
      URL.revokeObjectURL(formData.logo)
    }
    
    setFormData({
      tokenName: '',
      tokenSymbol: '',
      contractAddress: '',
      description: '',
      socialLinks: {
        twitter: '',
        telegram: '',
        discord: '',
        website: ''
      },
      dexPlatforms: {
        pumpswap: false,
        pancakeswap: false,
        raydium: false,
        jupiter: false,
        dexscreener: false
      },
      logo: null
    })
    setCurrentStep('form')
    setPreviewHtml('')
    setGeneratedUrl('')
    setError('')
  }

  return (
    <section id="generator" className={`${fullScreen ? 'py-8 px-4' : splitView ? 'h-full' : 'py-16 px-4'} relative`}>
      <div className={`${fullScreen || splitView ? 'max-w-full h-full' : 'max-w-7xl'} mx-auto`}>
        {/* Progress Steps */}
        {!splitView && (
        <div className="flex justify-center mb-12">
          <div className="glass-card px-8 py-6">
            <div className="flex items-center space-x-4 sm:space-x-8">
              <div className={`flex items-center ${currentStep === 'form' ? 'text-cyan-400' : currentStep === 'preview' || currentStep === 'published' ? 'text-green-400' : 'text-gray-500'}`}>
                <div className={`progress-step ${currentStep === 'form' ? 'active' : currentStep === 'preview' || currentStep === 'published' ? 'completed' : 'inactive'}`}>
                  {currentStep === 'preview' || currentStep === 'published' ? <Check className="w-4 h-4" /> : '1'}
                </div>
                <span className="ml-3 font-medium text-sm sm:text-base font-display">Create</span>
              </div>
              
              <div className={`w-12 sm:w-20 h-0.5 transition-all duration-500 ${currentStep === 'preview' || currentStep === 'published' ? 'bg-gradient-to-r from-green-400 to-cyan-400 shadow-lg' : 'bg-gray-600'}`} />
              
              <div className={`flex items-center ${currentStep === 'preview' ? 'text-cyan-400' : currentStep === 'published' ? 'text-green-400' : 'text-gray-500'}`}>
                <div className={`progress-step ${currentStep === 'preview' ? 'active' : currentStep === 'published' ? 'completed' : 'inactive'}`}>
                  {currentStep === 'published' ? <Check className="w-4 h-4" /> : '2'}
                </div>
                <span className="ml-3 font-medium text-sm sm:text-base font-display">Preview</span>
              </div>
              
              <div className={`w-12 sm:w-20 h-0.5 transition-all duration-500 ${currentStep === 'published' ? 'bg-gradient-to-r from-green-400 to-cyan-400 shadow-lg' : 'bg-gray-600'}`} />
              
              <div className={`flex items-center ${currentStep === 'published' ? 'text-green-400' : 'text-gray-500'}`}>
                <div className={`progress-step ${currentStep === 'published' ? 'completed' : 'inactive'}`}>
                  {currentStep === 'published' ? <Check className="w-4 h-4" /> : '3'}
                </div>
                <span className="ml-3 font-medium text-sm sm:text-base font-display">Publish</span>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="max-w-2xl mx-auto mb-8">
            <div className="glass-card border-red-500/30 bg-red-500/10 p-4 flex items-center animate-fade-in-up">
              <AlertCircle className="w-5 h-5 text-red-400 mr-3 flex-shrink-0" />
              <span className="text-red-300 text-sm sm:text-base">{error}</span>
            </div>
          </div>
        )}

        {/* Form Step */}
        {currentStep === 'form' && (
          <div className="max-w-2xl mx-auto">
            <div className="card animate-fade-in-up">
              <div className="flex items-center space-x-3 mb-8">
                <div className="p-2 bg-gradient-to-br from-cyan-500/20 to-purple-600/20 backdrop-blur-xl rounded-xl border border-cyan-500/30">
                  <Sparkles className="w-6 h-6 text-cyan-400" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-white font-display">Create Your Memecoin Site</h2>
              </div>
              
              <div className="space-y-8">
                {/* Basic Info */}
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label">Token Name *</label>
                      <input
                        type="text"
                        name="tokenName"
                        value={formData.tokenName}
                        onChange={handleInputChange}
                        className="input-field"
                        placeholder="e.g., Moon Doge"
                        required
                      />
                    </div>
                    <div>
                      <label className="form-label">Token Symbol *</label>
                      <input
                        type="text"
                        name="tokenSymbol"
                        value={formData.tokenSymbol}
                        onChange={handleInputChange}
                        className="input-field"
                        placeholder="e.g., $MOONDOGE"
                        required
                      />
                    </div>
                  </div>

                  {/* Contract Address */}
                  <div>
                    <label className="form-label">Contract Address *</label>
                    <input
                      type="text"
                      name="contractAddress"
                      value={formData.contractAddress}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder="Enter your token contract address"
                      required
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="form-label">Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      className="input-field"
                      rows="4"
                      placeholder="Go wild, describe the site as you want it to be"
                    />
                  </div>

                  {/* Logo Upload */}
                  <div>
                    <label className="form-label">Logo</label>
                    <div className="glass-card border-dashed border-2 border-cyan-500/30 p-6 text-center hover:border-cyan-400/50 transition-all duration-300 hover:bg-cyan-500/5">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        className="hidden"
                        id="logo-upload"
                      />
                      {formData.logo ? (
                        <div className="space-y-4">
                          <div className="flex justify-center">
                            <img 
                              src={URL.createObjectURL(formData.logo)} 
                              alt="Logo preview"
                              className="max-w-24 max-h-24 object-contain rounded-xl border border-cyan-500/30 shadow-lg"
                            />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-200">{formData.logo.name}</p>
                            <p className="text-xs text-gray-400">{(formData.logo.size / 1024 / 1024).toFixed(2)} MB</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, logo: null }))}
                            className="text-sm text-red-400 hover:text-red-300 transition-colors"
                          >
                            Remove logo
                          </button>
                        </div>
                      ) : (
                        <label htmlFor="logo-upload" className="cursor-pointer">
                          <Upload className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
                          <p className="text-gray-300 text-base mb-2">
                            Click to upload logo
                          </p>
                          <p className="text-sm text-gray-400">PNG, JPG, GIF, WebP up to 5MB</p>
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Social Links */}
                  <div>
                    <label className="form-label">Social Links</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <input
                        type="url"
                        value={formData.socialLinks.twitter}
                        onChange={(e) => handleSocialChange('twitter', e.target.value)}
                        className="input-field"
                        placeholder="Twitter URL"
                      />
                      <input
                        type="url"
                        value={formData.socialLinks.telegram}
                        onChange={(e) => handleSocialChange('telegram', e.target.value)}
                        className="input-field"
                        placeholder="Telegram URL"
                      />
                      <input
                        type="url"
                        value={formData.socialLinks.discord}
                        onChange={(e) => handleSocialChange('discord', e.target.value)}
                        className="input-field"
                        placeholder="Discord URL"
                      />
                      <input
                        type="url"
                        value={formData.socialLinks.website}
                        onChange={(e) => handleSocialChange('website', e.target.value)}
                        className="input-field"
                        placeholder="Website URL"
                      />
                    </div>
                  </div>

                  {/* DEX Platforms */}
                  <div>
                    <label className="form-label">Available on DEX Platforms</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {Object.entries(formData.dexPlatforms).map(([platform, checked]) => (
                        <label key={platform} className="glass-card flex items-center space-x-3 p-4 cursor-pointer hover:bg-cyan-500/5 transition-all duration-300">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => handleDexChange(platform, e.target.checked)}
                            className="w-4 h-4 text-cyan-500 bg-gray-800 border-gray-600 rounded focus:ring-cyan-500 focus:ring-2"
                          />
                          <span className="text-sm text-gray-300 capitalize font-medium">{platform}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Generate Button */}
                  <div className="pt-6">
                    <button
                      onClick={generatePreview}
                      disabled={isGenerating || !formData.tokenName || !formData.tokenSymbol || !formData.contractAddress}
                      className="btn-primary w-full text-lg"
                    >
                      {isGenerating ? (
                        <>
                          <Loader2 className="w-6 h-6 mr-3 animate-spin" />
                          <div className="text-left">
                            <div>Generating Preview...</div>
                            <div className="text-sm opacity-75">This may take 1-2 minutes</div>
                          </div>
                        </>
                      ) : (
                        <>
                          <Zap className="w-5 h-5 mr-3" />
                          Generate Preview
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}



        {/* Published Step */}
        {currentStep === 'published' && (
          <div className="max-w-2xl mx-auto">
            <div className="card animate-fade-in-up text-center">
              <div className="relative w-20 h-20 bg-gradient-to-br from-green-400 to-cyan-400 rounded-full flex items-center justify-center mx-auto mb-8 animate-pulse-glow">
                <div className="absolute inset-0 bg-gradient-to-br from-green-400/20 to-cyan-400/20 rounded-full animate-ping"></div>
                <Check className="w-10 h-10 text-white relative z-10" />
              </div>
              
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 font-display neon-text">Site Published Successfully!</h2>
              <p className="text-gray-300 mb-8 text-lg leading-relaxed">
                Your <span className="text-cyan-400 font-semibold">memecoin landing page</span> is now live and ready to 
                <span className="text-purple-400 font-semibold"> share with the world</span>.
              </p>
              
              <div className="glass-card p-6 mb-8 border border-green-500/30">
                <p className="text-sm text-green-300 mb-3 font-medium">Your site is live at:</p>
                <a
                  href={generatedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyan-400 font-mono text-lg sm:text-xl hover:text-cyan-300 transition-colors break-all block"
                >
                  {generatedUrl}
                </a>
                <div className="mt-4 flex justify-center">
                  <div className="px-3 py-1 bg-green-500/20 border border-green-500/30 rounded-full text-xs text-green-300 font-medium">
                    ✓ SSL Secured
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4">
                <a
                  href={generatedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary text-lg"
                >
                  <Star className="w-5 h-5 mr-2" />
                  Visit Your Site
                </a>
                <button
                  onClick={resetForm}
                  className="btn-secondary text-lg"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Create Another
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Diff Dialog */}
        {showDiffDialog && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="glass-card border border-cyan-500/30 max-w-md w-full animate-fade-in-up max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center p-6 border-b border-cyan-500/20">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-to-br from-cyan-500/20 to-purple-600/20 backdrop-blur-xl rounded-xl border border-cyan-500/30">
                    <Edit3 className="w-5 h-5 text-cyan-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white font-display">Edit Your Site</h3>
                </div>
                <button
                  onClick={() => {
                    setShowDiffDialog(false)
                    setDiffRequest('')
                  }}
                  className="text-gray-400 hover:text-cyan-400 transition-colors p-2 rounded-lg hover:bg-cyan-500/10"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6">
                <label className="form-label mb-4">
                  What would you like to change?
                </label>
                <textarea
                  value={diffRequest}
                  onChange={(e) => setDiffRequest(e.target.value)}
                  className="input-field resize-none"
                  rows="4"
                  placeholder="e.g., Make the buy button bigger and more prominent, change colors to green theme, add animation to the logo..."
                  disabled={isApplyingDiff}
                />
                
                <div className="mt-6 flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
                  <button
                    onClick={() => {
                      setShowDiffDialog(false)
                      setDiffRequest('')
                    }}
                    className="btn-secondary"
                    disabled={isApplyingDiff}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={applyDiff}
                    disabled={isApplyingDiff || !diffRequest.trim()}
                    className="btn-primary"
                  >
                    {isApplyingDiff ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Applying Changes...
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4 mr-2" />
                        Apply Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

export default SiteGenerator 